import React, { createContext, useContext, useState, useEffect } from 'react';

const SwimmingContext = createContext();

export function SwimmingProvider({ children }) {
  const [swimmers, setSwimmers] = useState(() => {
    const savedSwimmers = localStorage.getItem('swimmers');
    return savedSwimmers ? JSON.parse(savedSwimmers) : [];
  });

  const [results, setResults] = useState(() => {
    const savedResults = localStorage.getItem('results');
    return savedResults ? JSON.parse(savedResults) : {};
  });

  const [competitionStarted, setCompetitionStarted] = useState(() => {
    const savedStatus = localStorage.getItem('competitionStarted');
    return savedStatus ? JSON.parse(savedStatus) : false;
  });

  const [isCompetitionEnded, setIsCompetitionEnded] = useState(() => {
    const savedEnded = localStorage.getItem('isCompetitionEnded');
    return savedEnded ? JSON.parse(savedEnded) : false;
  });

  const [isRunning, setIsRunning] = useState(() => {
    const saved = localStorage.getItem('isRunning');
    return saved ? JSON.parse(saved) : false;
  });

  const [totalTime, setTotalTime] = useState(() => {
    const saved = localStorage.getItem('totalTime');
    return saved ? parseInt(saved) : 0;
  });

  const [poolLength, setPoolLength] = useState(() => {
    const saved = localStorage.getItem('poolLength');
    return saved ? parseInt(saved) : 50;
  });

  const [raceDistance, setRaceDistance] = useState(() => {
    const saved = localStorage.getItem('raceDistance');
    return saved ? parseInt(saved) : null;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const savedState = localStorage.getItem('swimmers');
      if (savedState) {
        setSwimmers(JSON.parse(savedState));
      }
      
      const savedResults = localStorage.getItem('results');
      if (savedResults) {
        setResults(JSON.parse(savedResults));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (swimmers.length > 0 || competitionStarted) {
      localStorage.setItem('swimmers', JSON.stringify(swimmers));
    }
  }, [swimmers, competitionStarted]);

  useEffect(() => {
    localStorage.setItem('results', JSON.stringify(results));
  }, [results]);

  useEffect(() => {
    localStorage.setItem('competitionStarted', JSON.stringify(competitionStarted));
  }, [competitionStarted]);

  useEffect(() => {
    localStorage.setItem('isCompetitionEnded', JSON.stringify(isCompetitionEnded));
  }, [isCompetitionEnded]);

  useEffect(() => {
    localStorage.setItem('isRunning', JSON.stringify(isRunning));
  }, [isRunning]);

  useEffect(() => {
    localStorage.setItem('totalTime', totalTime.toString());
  }, [totalTime]);

  useEffect(() => {
    localStorage.setItem('poolLength', poolLength.toString());
  }, [poolLength]);

  useEffect(() => {
    if (raceDistance) {
      localStorage.setItem('raceDistance', raceDistance.toString());
    } else {
      localStorage.removeItem('raceDistance');
    }
  }, [raceDistance]);

  useEffect(() => {
    if (isCompetitionEnded && totalTime > 0) {
      localStorage.setItem('totalTime', totalTime.toString());
    }
  }, [isCompetitionEnded, totalTime]);

  const startCompetition = (swimmersList = []) => {
    if (!Array.isArray(swimmersList)) {
      swimmersList = [];
    }
    const updatedSwimmers = [...swimmersList];
    setSwimmers(updatedSwimmers);
    setCompetitionStarted(true);
    setIsCompetitionEnded(false);
    setIsRunning(true);
    
    localStorage.setItem('swimmers', JSON.stringify(updatedSwimmers));
    localStorage.setItem('competitionStarted', 'true');
    localStorage.setItem('isCompetitionEnded', 'false');
    localStorage.setItem('isRunning', 'true');
  };

  const endCompetition = (finalTime) => {
    const timeToSave = finalTime || totalTime;
    setTotalTime(timeToSave);
    setCompetitionStarted(false);
    setIsCompetitionEnded(true);
    setIsRunning(false);
    localStorage.setItem('totalTime', timeToSave.toString());
    localStorage.setItem('competitionStarted', 'false');
    localStorage.setItem('isCompetitionEnded', 'true');
    localStorage.setItem('isRunning', 'false');
  };

  const resetCompetition = () => {
    if (window.confirm('Tem certeza que deseja iniciar uma nova competição? Todos os dados serão perdidos.')) {
      setSwimmers([]);
      setResults({});
      setCompetitionStarted(false);
      setIsCompetitionEnded(false);
      setIsRunning(false);
      setTotalTime(0);
      setPoolLength(50);
      setRaceDistance(null);
      
      localStorage.clear();
      localStorage.removeItem('hasShownLogo');
      window.location.href = '/';
    }
  };

  const addResult = (swimmer, lapTime) => {
    setResults(prev => {
      const newResults = { ...prev };
      const newLap = {
        time: lapTime,
        edited: false,
        originalTime: lapTime,
        timestamp: new Date().toISOString()
      };

      if (!newResults[swimmer]) {
        newResults[swimmer] = {
          laps: [],
          average: 0,
          lastUpdate: new Date().toISOString()
        };
      }

      const updatedLaps = [...(newResults[swimmer].laps || []), newLap];
      newResults[swimmer] = {
        laps: updatedLaps,
        average: updatedLaps.reduce((acc, lap) => acc + lap.time, 0) / updatedLaps.length,
        lastUpdate: new Date().toISOString()
      };

      localStorage.setItem('results', JSON.stringify(newResults));
      return newResults;
    });
  };

  const updateSwimmer = (oldName, newName, updatedData) => {
    setSwimmers(prevSwimmers => {
      const updatedSwimmers = prevSwimmers.map(name => name === oldName ? newName : name);
      localStorage.setItem('swimmers', JSON.stringify(updatedSwimmers));
      return updatedSwimmers;
    });

    if (oldName !== newName) {
      setResults(prevResults => {
        const newResults = { ...prevResults };
        if (newResults[oldName]) {
          newResults[newName] = newResults[oldName];
          delete newResults[oldName];
        }
        localStorage.setItem('results', JSON.stringify(newResults));
        return newResults;
      });
    }
  };

  const updateResult = (editedData) => {
    setResults(prevResults => {
      const newResults = { ...prevResults };
      if (newResults[editedData.oldName] || editedData.newName) {
        const processedLaps = editedData.laps.map(lap => {
          if (typeof lap === 'object') {
            return {
              time: lap.time || lap.lapTime,
              edited: true,
              originalTime: lap.originalTime || lap.time,
              timestamp: new Date().toISOString()
            };
          }
          return {
            time: lap,
            edited: false,
            originalTime: lap,
            timestamp: new Date().toISOString()
          };
        });

        const newData = {
          laps: processedLaps,
          average: processedLaps.reduce((acc, lap) => acc + lap.time, 0) / processedLaps.length,
          lastUpdate: new Date().toISOString()
        };

        newResults[editedData.newName] = newData;

        if (editedData.oldName !== editedData.newName) {
          delete newResults[editedData.oldName];
        }

        localStorage.setItem('results', JSON.stringify(newResults));

        updateSwimmer(editedData.oldName, editedData.newName, {
          ...newData,
          currentLapTime: processedLaps[processedLaps.length - 1]?.time || 0,
          totalDistance: processedLaps.length * (raceDistance || poolLength)
        });
      }
      return newResults;
    });
  };

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600000);
    const minutes = Math.floor((time % 3600000) / 60000);
    const seconds = Math.floor((time % 60000) / 1000);
    const centiseconds = Math.floor((time % 1000) / 10);

    const formattedHours = hours > 0 ? `${hours}:` : '';
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');
    const formattedCentiseconds = centiseconds.toString().padStart(2, '0');

    return `${formattedHours}${formattedMinutes}:${formattedSeconds}.${formattedCentiseconds}`;
  };

  return (
    <SwimmingContext.Provider value={{
      swimmers,
      setSwimmers,
      results,
      competitionStarted,
      isCompetitionEnded,
      isRunning,
      setIsRunning,
      totalTime,
      setTotalTime,
      poolLength,
      setPoolLength,
      raceDistance,
      setRaceDistance,
      startCompetition,
      endCompetition,
      resetCompetition,
      addResult,
      updateResult,
      formatTime,
      updateSwimmer
    }}>
      {children}
    </SwimmingContext.Provider>
  );
}

export function useSwimming() {
  return useContext(SwimmingContext);
}

export default SwimmingContext;